import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Lock,
  User,
  UserPlus,
  UserCheck,
  Building2,
  Globe,
  Phone,
  Briefcase,
  Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'registerWizard';

const initialAccountState = {
  username: '',
  displayName: '',
  email: '',
  password: '',
  confirmPassword: ''
};

const initialCompanyState = {
  name: '',
  website: '',
  size: '',
  phone: '',
  industry: ''
};

const RegisterPage = () => {
  const [accountData, setAccountData] = useState(initialAccountState);
  const [companyData, setCompanyData] = useState(initialCompanyState);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const { register } = useAuth();

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const saved = window.sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.accountData) {
          setAccountData({ ...initialAccountState, ...parsed.accountData });
        }
        if (parsed.companyData) {
          setCompanyData({ ...initialCompanyState, ...parsed.companyData });
        }
        if (parsed.step === 2) {
          setStep(2);
        }
      }
    } catch (storageError) {
      console.warn('Unable to load saved registration progress:', storageError);
    }
  }, []);

  const persistProgress = (notify = false) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, accountData, companyData })
      );
      if (notify) {
        setSaveMessage(`Progress saved • ${new Date().toLocaleTimeString()}`);
      }
    } catch (storageError) {
      console.warn('Unable to save registration progress:', storageError);
      if (notify) {
        setSaveMessage('Failed to save progress');
      }
    }
  };

  useEffect(() => {
    persistProgress(false);
  }, [step, accountData, companyData]);

  const clearProgress = () => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(STORAGE_KEY);
  };

  const validateAccountStep = () => {
    if (!accountData.username.trim()) {
      setError('Username is required.');
      return false;
    }

    if (!accountData.email.trim()) {
      setError('Email is required.');
      return false;
    }

    if (accountData.password !== accountData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    if (accountData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    setError('');
    if (!validateAccountStep()) {
      return;
    }
    setStep(2);
  };

  const handleSaveProgress = () => {
    persistProgress(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== 2) {
      handleNextStep();
      return;
    }

    setError('');

    if (!companyData.name.trim()) {
      setError('Company name is required.');
      return;
    }

    setLoading(true);

    const result = await register({
      username: accountData.username,
      email: accountData.email,
      password: accountData.password,
      displayName: accountData.displayName,
      company: {
        name: companyData.name,
        website: companyData.website,
        size: companyData.size,
        phone: companyData.phone,
        industry: companyData.industry
      }
    });

    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    clearProgress();
    setSaveMessage('');
    setLoading(false);
  };

  const steps = [
    {
      id: 1,
      title: 'Account',
      description: 'Create your login credentials'
    },
    {
      id: 2,
      title: 'Company',
      description: 'Tell us about your organization'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <UserPlus className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Email Marketing Portal
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {steps.map((item) => (
                <div key={item.id} className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                        step === item.id
                          ? 'bg-blue-600 text-white'
                          : step > item.id
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {step > item.id ? '✓' : item.id}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {step === 1 && (
              <>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                    Username
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your username"
                      value={accountData.username}
                      onChange={(e) =>
                        setAccountData((prev) => ({ ...prev, username: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    Display Name <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserCheck className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="displayName"
                      name="displayName"
                      type="text"
                      autoComplete="name"
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your display name"
                      value={accountData.displayName}
                      onChange={(e) =>
                        setAccountData((prev) => ({ ...prev, displayName: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your email"
                      value={accountData.email}
                      onChange={(e) =>
                        setAccountData((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Create a password"
                      value={accountData.password}
                      onChange={(e) =>
                        setAccountData((prev) => ({ ...prev, password: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm your password"
                      value={accountData.confirmPassword}
                      onChange={(e) =>
                        setAccountData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value
                        }))
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                    Company name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your company name"
                      value={companyData.name}
                      onChange={(e) =>
                        setCompanyData((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-700">
                    Company website <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Globe className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="companyWebsite"
                      name="companyWebsite"
                      type="url"
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com"
                      value={companyData.website}
                      onChange={(e) =>
                        setCompanyData((prev) => ({ ...prev, website: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="companySize" className="block text-sm font-medium text-gray-700">
                      Company size
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Briefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <select
                        id="companySize"
                        name="companySize"
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={companyData.size}
                        onChange={(e) =>
                          setCompanyData((prev) => ({ ...prev, size: e.target.value }))
                        }
                      >
                        <option value="">Select size</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="500+">500+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700">
                      Company phone <span className="text-gray-400 text-xs">(optional)</span>
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="companyPhone"
                        name="companyPhone"
                        type="tel"
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                        value={companyData.phone}
                        onChange={(e) =>
                          setCompanyData((prev) => ({ ...prev, phone: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="companyIndustry" className="block text-sm font-medium text-gray-700">
                    Industry
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="companyIndustry"
                      name="companyIndustry"
                      type="text"
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. Marketing agency"
                      value={companyData.industry}
                      onChange={(e) =>
                        setCompanyData((prev) => ({ ...prev, industry: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {saveMessage && (
              <p className="text-xs text-gray-500 text-right">{saveMessage}</p>
            )}

            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {step === 2 && (
                  <button
                    type="button"
                    className="flex-1 min-w-[140px] inline-flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    onClick={() => setStep(1)}
                  >
                    Back to account
                  </button>
                )}
                <button
                  type="button"
                  className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 py-2 px-4 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                  onClick={handleSaveProgress}
                >
                  <Save className="h-4 w-4" />
                  Save progress
                </button>
              </div>
              <button
                type={step === steps.length ? 'submit' : 'button'}
                onClick={step === steps.length ? undefined : handleNextStep}
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {step === steps.length
                  ? loading
                    ? 'Creating account...'
                    : 'Create account'
                  : 'Continue to company'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Sign in
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
